// Month math for calendar views. Weeks start on Monday (Italian convention).
import { MONTHS, addDays, parseDate, weekday } from './dates.ts';

export const WEEKDAY_SHORT = ['L', 'M', 'M', 'G', 'V', 'S', 'D'] as const; // Monday-first
export const WEEKDAY_ABBR = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'] as const;
/** JS weekday numbers (0 = Sunday) in Monday-first display order. */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

export const monthOf = (date: string) => date.slice(0, 7);

export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
}

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const name = MONTHS[m - 1]!;
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${y}`;
}

export function monthBounds(month: string) {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { first: `${month}-01`, last: `${month}-${String(last).padStart(2, '0')}` };
}

/** Dates shown in a month view: full weeks (Mon–Sun) covering the month. */
export function monthMatrix(month: string): string[][] {
  const { first, last } = monthBounds(month);
  const offset = (weekday(first) + 6) % 7; // days since Monday
  let cursor = addDays(first, -offset);
  const weeks: string[][] = [];
  while (cursor <= last || weeks.length < 5) {
    const week: string[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(cursor);
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
    if (cursor > last && weeks.length >= 5) break;
  }
  return weeks;
}

/** Visible range of a month view (for API queries). */
export function monthViewRange(month: string) {
  const weeks = monthMatrix(month);
  return { from: weeks[0]![0]!, to: weeks[weeks.length - 1]![6]! };
}

/** Monday–Sunday week containing a date. */
export function weekOf(date: string): string[] {
  const start = addDays(date, -((weekday(date) + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export const dayNumber = (date: string) => parseDate(date).getUTCDate();
